import React, { useState } from "react";
import { IconButton, Tooltip, Chip } from "@material-ui/core";
import SweetAlert from "react-bootstrap-sweetalert";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardTitle from "components/Card/CardTitle";
import CardFooter from "components/Card/CardFooter.js";
import VaultCardBody from "components/Card/VaultCardBody";
import EditVaultDialogue from "views/Components/EditVaultDialogue";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import LockOpen from "@material-ui/icons/LockOpen";
import sweetAlertStyle from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import { getCompanyIconUrl } from "views/Components/Constants.js";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

const useSweetAlertStyle = makeStyles(sweetAlertStyle);

const typeChip = (keyType) => {
  if (keyType === "default") {
    return (
      <Chip
        size="medium"
        label="Default"
        style={{
          background: "#20677b6b",
          color: "#20677b",
          fontWeight: 600,
          borderRadius: "4px",
        }}
      />
    );
  }
  if (keyType === "backup") {
    return (
      <Chip
        size="medium"
        label="Backup"
        style={{
          background: "#ff91005d",
          color: "#FF8F00",
          fontWeight: 600,
          borderRadius: "4px",
        }}
      />
    );
  }
  return null; // nothing for 'none'
};

const VaultCard = ({
  apiKeyObject,
  handleDeleteAsset,
  handleUpdateAsset,
  onMakeDefault,
  onMakeBackup,
  hasDefaultKey,
  hasBackupKey,
}) => {
  const sweetAlertClasses = useSweetAlertStyle();
  const [lock, setLock] = useState(true);
  const [modelDetails, setModelDetails] = useState({
    _id: "",
    model: "",
    name: "",
    description: "",
    old_api_key: "",
    new_api_key: "",
    key_type: "none",
  });
  const [editAssetModal, setEditAssetModal] = useState(false);
  const [alert, setAlert] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const isDefault = (apiKeyObject.key_type || "none") === "default";
  const isBackup = (apiKeyObject.key_type || "none") === "backup";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKeyObject.key);
      // If you have a snackbar in parent, you could emit a callback here
    } catch {
      // no-op
    }
  };

  const handleLock = () => setLock(!lock);

  const handleEditAPIKey = () => {
    setModelDetails({
      ...modelDetails,
      _id: apiKeyObject._id,
      company: apiKeyObject.company,
      model: apiKeyObject.model,
      name: apiKeyObject.name,
      description: apiKeyObject.description,
      old_api_key: apiKeyObject.key,
      key_type: apiKeyObject.key_type || "none",
    });
    setEditAssetModal(true);
  };

  const deleteAlert = (asset) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => handleDelete(asset)}
        onCancel={hideAlert}
        confirmBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.success
        }
        cancelBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.danger
        }
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
      >
        This action will permanently delete the API Key and will not be
        recoverable.
      </SweetAlert>
    );
  };

  const handleDelete = async (asset) => {
    await handleDeleteAsset(asset);
    hideAlert();
  };

  const hideAlert = () => setAlert(null);

  return (
    <>
      {alert}
      <EditVaultDialogue
        open={editAssetModal}
        handleEditDialogueClose={() => setEditAssetModal(false)}
        modelDetails={modelDetails}
        setModelDetails={setModelDetails}
        handleUpdateAsset={handleUpdateAsset}
      />
      <Card
        style={{
          marginTop: "40px",
          border: `2px solid ${
            isBackup ? "#FF8F00" : isDefault ? "#20677b" : "#fff"
          }`,
        }}
      >
        <CardHeader icon text>
          <CardTitle color="quCreateColor">{apiKeyObject.name}</CardTitle>
        </CardHeader>

        <VaultCardBody
          image={getCompanyIconUrl(apiKeyObject.company)}
          locked={lock}
          handleLock={handleLock}
          apiKeyObject={apiKeyObject}
          company={apiKeyObject.company}
        />

        <CardFooter
          stats
          style={{
            marginTop: "0px",
            paddingBottom: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Bottom-left: key type chip */}
          <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>
            {typeChip(apiKeyObject.key_type || "none")}
          </div>

          {/* Center/right: actions (only when unlocked) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!lock && (
              <>
                <Tooltip title="Lock API Key">
                  <LockOpen
                    style={{
                      color: "#435966",
                      marginLeft: "10px",
                      cursor: "pointer",
                    }}
                    onClick={handleLock}
                  />
                </Tooltip>
                <Tooltip title="Copy API Key">
                  <IconButton size="small" onClick={handleCopy}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit API Key">
                  <EditIcon
                    style={{
                      color: "#435966",
                      marginLeft: "10px",
                      cursor: "pointer",
                    }}
                    onClick={handleEditAPIKey}
                  />
                </Tooltip>
                <Tooltip title="Delete API Key">
                  <DeleteIcon
                    style={{
                      color: "#435966",
                      marginLeft: "10px",
                      cursor: "pointer",
                    }}
                    onClick={() => deleteAlert(apiKeyObject)}
                  />
                </Tooltip>
                {/* Role menu */}
                <Tooltip title="More actions">
                  <IconButton size="small" onClick={openMenu}>
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={closeMenu}
                >
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      onMakeDefault && onMakeDefault();
                    }}
                    disabled={isDefault} // already default
                  >
                    Make Default
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      onMakeBackup && onMakeBackup();
                    }}
                    disabled={isBackup || isDefault || !hasDefaultKey}
                    // can't be backup if it's default; must have a default first
                  >
                    Make Backup
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
        </CardFooter>
      </Card>
    </>
  );
};

export default VaultCard;
